import { defineStore } from 'pinia';
import { useMapStoreData } from './data';

type EventListItem = Record<string, any>;

export const useEventListStore = defineStore('eventListStore', {
	state: () => ({
		items: [] as EventListItem[],
		activeEventPresentation: null as EventListItem | null,
		isLoading: false as boolean,
		error: null as string | null,
		eventsTimestamp: 0 as number,
		loadedLanguage: '' as string,
		pollingIntervalId: null as ReturnType<typeof setInterval> | null,
		isRefreshing: false as boolean,
	}),
	actions: {
		buildDataPath() {
			const mapData = useMapStoreData();
			return mapData.publicUrl + 'data/' + mapData.destinationId + '/';
		},
		normalizeItems(rawData: any) {
			if (Array.isArray(rawData)) {
				return rawData;
			}
			if (rawData && Array.isArray(rawData.items)) {
				return rawData.items;
			}
			if (rawData && typeof rawData === 'object') {
				return Object.values(rawData);
			}
			return [];
		},
		async fetchEventsJsonByLanguage(language: string) {
			const basePath = this.buildDataPath();
			const response = await fetch(basePath + 'events_' + language + '.json');
			if (response.ok) {
				return response.json();
			}
			const fallbackResponse = await fetch(basePath + 'events_en.json');
			if (!fallbackResponse.ok) {
				throw new Error('Failed to load EventList JSON for language "' + language + '" and fallback "en".');
			}
			return fallbackResponse.json();
		},
		async loadEventsForLanguage(language: string | null = null) {
			const mapData = useMapStoreData();
			const lang = language || mapData.currentLanguage || 'en';
			this.isLoading = true;
			this.error = null;
			try {
				const jsonData = await this.fetchEventsJsonByLanguage(lang);
				this.items = this.normalizeItems(jsonData);
				this.loadedLanguage = lang;
			} catch (error: any) {
				this.error = error?.message || 'Failed to load EventList JSON.';
			} finally {
				this.isLoading = false;
			}
		},
		async checkForUpdates() {
			const mapData = useMapStoreData();
			if (this.isRefreshing || !mapData.destinationId) {
				return;
			}
			this.isRefreshing = true;
			try {
				const response = await fetch(this.buildDataPath() + 'timestamp_events');
				if (!response.ok) {
					return;
				}
				const timestampText = await response.text();
				const timestamp = parseInt(timestampText);
				if (!Number.isNaN(timestamp) && timestamp > this.eventsTimestamp) {
					this.eventsTimestamp = timestamp;
					await this.loadEventsForLanguage();
				}
			} catch {
				// Keep EventList resilient; next poll retries.
			} finally {
				this.isRefreshing = false;
			}
		},
		async syncOnLanguageChange() {
			const mapData = useMapStoreData();
			const currentLanguage = mapData.currentLanguage || 'en';
			if (this.loadedLanguage !== currentLanguage) {
				await this.loadEventsForLanguage(currentLanguage);
			}
		},
		startPolling(intervalMs = 15000) {
			this.stopPolling();
			this.pollingIntervalId = setInterval(() => {
				this.checkForUpdates();
			}, intervalMs);
		},
		stopPolling() {
			if (this.pollingIntervalId) {
				clearInterval(this.pollingIntervalId);
				this.pollingIntervalId = null;
			}
		},
		setActiveEventPresentation(eventItem: EventListItem | null) {
			this.activeEventPresentation = eventItem;
		},
	},
});
